import { Typography } from "@mui/material";
import { ContentGroupFormatting } from "../../pages/playground/types";
import { formatPhrases } from "../../utils/content-group-formatting";
import { sanitizePhrase } from "../../utils/phrase-formatting";

// ── Props ────────────────────────────────────────────────────────────

interface PhraseGroupProps {
  phrases: string[];
  formatting: ContentGroupFormatting;
  mode: "rendered" | "preview";
}

// ── Component ────────────────────────────────────────────────────────

export default function PhraseGroup({ phrases, formatting, mode }: PhraseGroupProps) {
  if (!phrases || phrases.length === 0) return null;

  // Sanitize each phrase BEFORE formatting so tags like <li>, <p> from
  // the formatting config are preserved while user-injected HTML is stripped.
  const safePhrases = phrases.map((p) => sanitizePhrase(p));

  if (mode === "rendered") {
    const raw = formatPhrases(safePhrases, formatting);
    // When the formatter already produces <li>/<ul><li> markup, the newlines
    // between list items are structural whitespace, not soft line breaks —
    // injecting <br> would create invalid markup inside the list.
    const producesListMarkup =
      formatting.phraseWrapTag === "<li>" ||
      formatting.bulletPrefix === "<ul><li>" ||
      formatting.bulletPrefix === "<li>";
    const html = producesListMarkup ? raw : raw.replace(/\n/g, "<br>");
    return (
      <div
        dangerouslySetInnerHTML={{ __html: html }}
        style={{ lineHeight: 1.6 }}
      />
    );
  }

  // preview: formatted string with visible tags
  const text = formatPhrases(safePhrases, formatting);
  return (
    <Typography
      variant="body2"
      component="pre"
      sx={{
        whiteSpace: "pre-wrap",
        fontFamily: "monospace",
        fontSize: "0.8rem",
        m: 0,
        lineHeight: 1.6,
      }}
    >
      {text}
    </Typography>
  );
}
